import { useSyncExternalStore } from "react";

/*
 * "Updated, tap to refresh". The build knows its own version (VITE_APP_VERSION, the commit SHA); every api response
 * says which version answered (X-App-Version). When they differ a new release is live: show a quiet banner and let
 * the reader reload when it suits them. Never reload by itself. Until then this tab keeps working: the old release's
 * assets stay on the server (ops/deploy.sh keeps the last five).
 */
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION || "dev";

const UNVERSIONED = new Set(["dev", "test", ""]);
let newer = false;
const listeners = new Set<() => void>();

/** Called with the X-App-Version of each api response. */
export function noteServerVersion(v: string | null) {
  if (newer || !v || UNVERSIONED.has(v) || UNVERSIONED.has(APP_VERSION) || v === APP_VERSION) return;
  newer = true;
  for (const l of listeners) l();
}

export function useNewVersion(): boolean {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => newer, () => false);
}

/** A tab left open for days may make no api calls: ask once whenever it comes back to the front. */
export function checkVersionOnFocus(check: () => Promise<unknown>) {
  let last = 0;
  const go = () => {
    if (document.visibilityState !== "visible" || Date.now() - last < 30_000) return;
    last = Date.now();
    void check().catch(() => {});
  };
  document.addEventListener("visibilitychange", go);
  window.addEventListener("focus", go);
}
